import React, { useMemo } from 'react';
import { useQuery } from 'react-query';
import Alert from '@amzn/meridian/alert';
import Column from '@amzn/meridian/column';
import Heading from '@amzn/meridian/heading';
import Row from '@amzn/meridian/row';
import Button from '@amzn/meridian/button';
import {
  ColumnDefinition,
  CoreTable,
  ListResponse,
  CoreCopyButton,
} from '@amzn/dots-core-ui';
import DownloadTokens from '@amzn/meridian-tokens/base/icon/download-large';
import Icon from '@amzn/meridian/icon';
import { BuildFile, useClient, ProjectName, BuildVersion } from '../../../api';

// Helper function to format file size
const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(2)} KB`;
  const mb = kb / 1024;
  if (mb < 1024) return `${mb.toFixed(2)} MB`;
  const gb = mb / 1024;
  return `${gb.toFixed(2)} GB`;
};

export const buildFilesColumnDefinitionsFactory =
  (): ColumnDefinition<BuildFile>[] => [
    {
      id: 'filename',
      header: 'Filename',
      item: (buildFile): string => `${buildFile.filename}`,
    },
    {
      id: 'url',
      header: 'Download',
      item: (buildFile): JSX.Element => (
        <Button type="icon" onClick={() => window.open(buildFile.url)}>
          <Icon tokens={DownloadTokens}>Download</Icon>
        </Button>
      ),
    },
    {
      id: 'size',
      header: 'Size',
      item: (buildFile): string => formatFileSize(buildFile.size),
    },
    {
      id: 'md5',
      header: 'MD5 Checksum',
      item: (buildFile): JSX.Element => (
        <Row spacing="200" alignmentVertical="center">
          <code>{buildFile.md5}</code>
          <CoreCopyButton text={buildFile.md5} />
        </Row>
      ),
    },
    {
      id: 'modified',
      header: 'Modified',
      item: (buildFile): string =>
        buildFile.modified instanceof Date
          ? buildFile.modified.toLocaleString()
          : new Date(buildFile.modified).toLocaleString(),
    },
    {
      id: 'created',
      header: 'Created',
      item: (buildFile): string =>
        buildFile.created instanceof Date
          ? buildFile.created.toLocaleString()
          : new Date(buildFile.created).toLocaleString(),
    },
  ];

export type buildFileListProps = {
  projectName: ProjectName;
  buildVersion: BuildVersion;
};

export const BuildFilesList = ({
  projectName,
  buildVersion,
}: buildFileListProps): JSX.Element => {
  const queryKey = useMemo(
    () => ['buildFiles', projectName, buildVersion],
    [projectName, buildVersion]
  );
  const { projects } = useClient();
  const { data, isError, isLoading, isSuccess, error } = useQuery<
    ListResponse<BuildFile>,
    Error
  >(queryKey, () => projects.getBuildFiles(projectName, buildVersion));

  return (
    <Column spacingInset="400">
      <Row>
        <Heading level={3}>Build Files</Heading>
      </Row>
      {isError && !error?.message.startsWith('Not Found:') && (
        <Alert size="medium" type="error">
          <span>Failed to load files:</span>
          <span> </span>
          <span>{error?.message}</span>
        </Alert>
      )}
      {isSuccess && data && !data.content.length && (
        <Alert size="medium" type="warning">
          No files found for this build.
        </Alert>
      )}
      <CoreTable
        columnDefinitions={buildFilesColumnDefinitionsFactory()}
        getItemId={(item: BuildFile) => item.id}
        isLoading={isLoading}
        items={data?.content}
      />
    </Column>
  );
};
